import { Component, h, State, Prop, Method } from '@stencil/core';
import { UserInstance, Genders, CustomField, PhoneNumberFieldEventData, FormFieldTypes } from './interfaces';
import CameraIcon from "../../assets/svg/camera.svg";

/**
 * The SallaUserProfile is a versatile user profile form 
 * generator within the Salla platform, offering localization support, 
 * customizable fields, and seamless integration with the Salla API. 
 * It dynamically fetches translated strings for labels and messages from the 
 * Salla localization object, allowing for a multilingual user interface. Users 
 * can extend the default form with custom fields through the `customFields`
 * property and the setCustomFields method. The component manages user data 
 * retrieval from the Salla configuration and includes fields for first name, 
 * last name, birthday, gender, email, mobile, and custom additions. Form 
 * validation and submission are handled through various event handlers, ensuring 
 * proper field handling and user input validation. The component supports file 
 * uploads for photo fields and includes a dedicated phone number input field. 
 * The componentWillLoad lifecycle method fetches additional user profile 
 * information from the API during initialization. Overall, this component 
 * provides an efficient and adaptable solution for creating user profiles with a 
 * rich set of features and customization options.
 */
@Component({
  tag: 'salla-user-profile',
  styleUrl: 'salla-user-profile.scss',
})
export class SallaUserProfile {

  // Constructor
  constructor() {
    // Localization setup when the language is loaded
    salla.lang.onLoaded(() => {
      // Assigning translated strings to state properties
      // These translations are fetched from the localization object
      this.first_name_trans = salla.lang.get('pages.profile.first_name')
      this.last_name_trans = salla.lang.get('pages.profile.last_name')
      this.birthday_trans = salla.lang.get('pages.profile.birthday')
      this.birthday_placeholder_trans = salla.lang.get('pages.profile.birthday_placeholder')
      this.gender_trans = salla.lang.get('pages.profile.gender')
      this.gender_placeholder_trans = salla.lang.get('pages.profile.gender_placeholder')
      this.male_trans = salla.lang.get('pages.profile.male')
      this.female_trans = salla.lang.get('pages.profile.female')
      this.email_trans = salla.lang.get('common.elements.email')
      this.mobile_trans = salla.lang.get('common.elements.mobile')
      this.save_btn_trans = salla.lang.get('common.elements.save')
      this.drag_and_drop_trans = salla.lang.get('common.uploader.drag_and_drop')
      this.browse_trans = salla.lang.get('common.uploader.browse')
      this.email_required_trans = salla.lang.get('pages.checkout.email_required')
      this.invalid_email_trans = salla.lang.get('pages.error.invalid_value', {attribute: "email"})
    })
  }

  /**
   * Custome fields to be rendered in addition to the default ones.
   */
  @Prop() customFields: string;

  /**
   * The minimum allowed age for a user. Users with a birthdate indicating an age less than this value will be considered invalid.
   * Defaults to 10.
   * @type {number}
   */
  @Prop() minAge: number = 10;

  // Component State
  @State() userData: UserInstance;
  @State() userDefinedFields: CustomField[];
  @State() disableAction: boolean = false;
  @State() isLoading: boolean = true;

  // Translated Strings State
  @State() first_name_trans = salla.lang.get('pages.profile.first_name')
  @State() last_name_trans = salla.lang.get('pages.profile.last_name')
  @State() birthday_trans = salla.lang.get('pages.profile.birthday')
  @State() birthday_placeholder_trans = salla.lang.get('pages.profile.birthday_placeholder')
  @State() gender_trans = salla.lang.get('pages.profile.gender')
  @State() gender_placeholder_trans = salla.lang.get('pages.profile.gender_placeholder')
  @State() male_trans = salla.lang.get('pages.profile.male')
  @State() female_trans = salla.lang.get('pages.profile.female')
  @State() email_trans = salla.lang.get('common.elements.email')
  @State() mobile_trans = salla.lang.get('common.elements.mobile')
  @State() save_btn_trans = salla.lang.get('common.elements.save')
  @State() drag_and_drop_trans = salla.lang.get('common.uploader.drag_and_drop')
  @State() browse_trans = salla.lang.get('common.uploader.browse')
  @State() email_required_trans = salla.lang.get('pages.checkout.email_required')
  @State() invalid_email_trans = salla.lang.get('pages.error.invalid_value', {attribute: "email"})

  /**
   * Sets custom fields for the component. Can be handy for non HTML usage.
   *
   * @param fields - An array of custom fields.
   */
  @Method()
  async setCustomFields(fields: CustomField[]): Promise<void> {
    this.userDefinedFields = fields;
  }

  // Event handler for phone number field changes
  private phoneNumberFieldEventHandler(data) {
    if(!data.detail.number){
      return this.disableAction = true;
    }
    this.userData.phone.number = parseInt(data.detail.number)
    this.userData.phone.country = data.detail.country_code
    this.disableAction = false;
  }

  // Event handler for generic field changes
  private handleFieldChange(key: string, event, required = false) {
    if (event.target.value) {
      this.userData[key] = event.target.value;
      this.disableAction = false;
    }else {
      if(required){
        this.disableAction = true;
      }
    }
  }

  // Event handler for file upload changes
  private handleOnFileAdded(key: string, data) {
    // TODO: the key in here, shall be not have whitespace. or the key for 
    // the field shall be provided with one of the properties of the custom field
    if (!data.detail.error) {
      this.userData[key] = data.detail.file
      this.disableAction = false;
    }else {
      this.disableAction = true;
    }
  }

  // Event handler for email input changes
  private handleEmailInput(key: string, event) {
    const emailErrorDisplaySection = document.getElementById('email-error');
    const email = event.target.value;
    if (!email) {
      this.disableAction = true;
      return emailErrorDisplaySection.textContent = this.email_required_trans
    }
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailPattern.test(email)) {
      this.disableAction = true;
      return emailErrorDisplaySection.textContent = this.invalid_email_trans;
    }
    emailErrorDisplaySection.textContent = '';
    this.userData[key] = email
    this.disableAction = false;
  }

  // Helper method to trim language code from URL
  private trimLanguageCodeFromUrl(url: string, languageCode: string): string {
    const pattern = new RegExp(`^(https?://[^/]+)\\/${languageCode}(.*)`, 'i');

    const match = url.replace(/\/+$/, '').match(pattern);

    if (match && match.length >= 2) {
      const [, baseUrl, restOfUrl] = match;
      return baseUrl + restOfUrl;
    }

    return url;
  }

  // Helper method to get file upload URL
  private getFileUploadUrl() {
    return `${this.trimLanguageCodeFromUrl(salla.config.get('store.url'), salla.config.get('user.language_code'))}/upload-image`
  }

  // Render custom fields based on user-defined fields
  private renderCustomFields() {
    if (!!this.userDefinedFields) {
      return this.userDefinedFields.map((field: CustomField) => {
          return < div class="s-user-profile-field">
            <label htmlFor={`${field.id}`} class="s-user-profile-field-label">
              {field.label}
            </label>
            {
              field.type == FormFieldTypes.Photo ?
                <salla-file-upload instant-upload value={field.value}
                  url={this.getFileUploadUrl()} name={`custom_fields[${field.id}]`}
                  payload-name="image" height="120px" onAdded={(data) => this.handleOnFileAdded(`custom_fields[${field.id}]`, data)}>
                  <div class="s-user-profile-filepond-placeholder">
                    <span class="s-user-profile-filepond-placeholder-icon"><i innerHTML={CameraIcon}></i></span>
                    <p class="s-user-profile-filepond-placeholder-text">{this.drag_and_drop_trans}</p>
                    <span class="filepond--label-action">{this.browse_trans}</span>
                  </div>
                </salla-file-upload>
                :
                <input type={field.type} id={`${field.id}`} value={field.value}
                  onChange={(event) => this.handleFieldChange(`custom_fields[${field.id}]`, event)}
                  name={`custom_fields[${field.id}]`} class="form-input" required={field.required} />
            }
          </div>
        })
    }
    return ""
  }

  // Submit form method
  private submitForm(event: Event): void {
    event.preventDefault();
    this.disableAction = true;

    let payload = Object.assign({}, this.userData)
    delete payload.phone
    //@ts-ignore
    payload["phone"] = this.userData.phone.number
    payload["country_code"] = this.userData.phone.country
    return salla.api.profile.update(payload).finally(() => this.disableAction = false)
  }

  private getBirthDateRestriction() {
    const now = new Date();
    const pastYear = now.getFullYear() - this.minAge;
    now.setFullYear(pastYear);
    return now;
  }

  private fetchData() {
    // Load user-defined fields and initial user data
    if (this.customFields) this.userDefinedFields = JSON.parse(this.customFields);
    return salla.api.profile.info().then(resp => {
      this.userData = resp.data;
    }).finally(() => {
      this.isLoading = false;
    })
  }

  private renderLoadingSection() {
    return <div class="s-user-profile-skeleton-wrapper">
      {Array.from({ length: 6 }, (_, i) => <salla-skeleton class="skeleton-item" width="100%" height="50px" key={i}></salla-skeleton>)}
    </div>
  }


  render() {
    if (this.isLoading) {
      return this.renderLoadingSection();
    }
    return <form onSubmit={(event) => this.submitForm(event)}>
        <div class="s-user-profile-wrapper">
          {/* First name */}
          <div class="s-user-profile-field">
            <label htmlFor="first-name" class="s-user-profile-field-label">
              {this.first_name_trans}
            </label>
            <input type="text" name="first_name" value={this.userData.first_name} id="first-name" required
              autocomplete="first_name" class="form-input" onChange={(event) => this.handleFieldChange("first_name", event)} />
          </div>

          {/* Last name */}
          <div class="s-user-profile-field">
            <label htmlFor="last-name" class="s-user-profile-field-label">
              {this.last_name_trans}
            </label>
            <input type="text" name="last_name" value={this.userData.last_name} id="last-name" required
              autocomplete="last_name" class="form-input" onChange={(event) => this.handleFieldChange("last_name", event)} />
          </div>

          {/* Birthday */}
          <div class="s-user-profile-field">
            <label htmlFor="birthday" class="s-user-profile-field-label">
              {this.birthday_trans}
            </label>
            <salla-datetime-picker dateFormat="Y-m-d" value={this.userData.birthday}
              placeholder={this.birthday_placeholder_trans} required maxDate={this.getBirthDateRestriction()}
              name="birthday" onPicked={(event) => this.handleFieldChange("birthday", event)}></salla-datetime-picker>
          </div>

          {/* Gender */}
          <div class="s-user-profile-field">
            <label htmlFor="gender" class="s-user-profile-field-label">
              {this.gender_trans}
            </label>
            <select class="form-input" name="gender" required onChange={(event) => this.handleFieldChange("gender", event)}>
              <option value="">{this.gender_placeholder_trans}</option>
              <option value={Genders.Male} selected={this.userData.gender == Genders.Male}>
                {this.male_trans}
              </option>
              <option value={Genders.Female} selected={this.userData.gender == Genders.Female}>
                {this.female_trans}
              </option>
            </select>
          </div>

          {/* Email */}
          <div class="s-user-profile-field">
            <label htmlFor="email" class="s-user-profile-field-label">
              {this.email_trans}
            </label>
            <input type="email" name="email" value={this.userData.email} id="email" class="form-input" required onInput={(event) => this.handleEmailInput("email", event)} />
            <p id="email-error" class="s-user-profile-field-error"></p>
          </div>

          {/* Mobile */}
          <div class="s-user-profile-field">
            <label htmlFor="international-mobile" class="s-user-profile-field-label">
              {this.mobile_trans}
            </label>
            <salla-tel-input
              name='international-mobile'
              country-code={this.userData.phone.country}
              phone={`${this.userData.phone.number}`}
              onPhoneEntered={(data) => this.phoneNumberFieldEventHandler(data as unknown as PhoneNumberFieldEventData)}
            ></salla-tel-input>
          </div>

          {/* Custom Fields */}
          {this.renderCustomFields()}

          {/* Action */}
        </div>
          <salla-button type="submit" loading={this.disableAction} disabled={this.disableAction} loader-position="end" class="s-user-profile-submit">
            {this.save_btn_trans}
          </salla-button>
      </form>;
  }

  componentWillLoad() {
    this.fetchData()
  }

}
